export const FINANCIAL_STATUS = {
  SCHEDULED: "PROGRAMADO",
  FORECAST: "PREVISTO",
  OPEN: "EM_ABERTO",
  PARTIAL: "PARCIAL",
  PAID: "PAGO",
  RECEIVED: "RECEBIDO",
  OVERDUE: "VENCIDO",
  CANCELLED: "CANCELADO"
} as const;

export type AccountKind = "payables" | "receivables";
export type FinancialStatus = typeof FINANCIAL_STATUS[keyof typeof FINANCIAL_STATUS];

export const FINANCIAL_STATUS_LABELS: Record<FinancialStatus, string> = {
  PROGRAMADO: "Programado",
  PREVISTO: "Previsto",
  EM_ABERTO: "Em aberto",
  PARCIAL: "Parcial",
  PAGO: "Pago",
  RECEBIDO: "Recebido",
  VENCIDO: "Vencido",
  CANCELADO: "Cancelado"
};

const aliases: Record<string, FinancialStatus> = {
  SCHEDULED: FINANCIAL_STATUS.SCHEDULED,
  PROGRAMADO: FINANCIAL_STATUS.SCHEDULED,
  FORECAST: FINANCIAL_STATUS.FORECAST,
  PREVISTO: FINANCIAL_STATUS.FORECAST,
  OPEN: FINANCIAL_STATUS.OPEN,
  EM_ABERTO: FINANCIAL_STATUS.OPEN,
  PARTIAL: FINANCIAL_STATUS.PARTIAL,
  PARCIAL: FINANCIAL_STATUS.PARTIAL,
  PAID: FINANCIAL_STATUS.PAID,
  PAGO: FINANCIAL_STATUS.PAID,
  RECEIVED: FINANCIAL_STATUS.RECEIVED,
  RECEBIDO: FINANCIAL_STATUS.RECEIVED,
  OVERDUE: FINANCIAL_STATUS.OVERDUE,
  VENCIDO: FINANCIAL_STATUS.OVERDUE,
  VENCIDA: FINANCIAL_STATUS.OVERDUE,
  CANCELLED: FINANCIAL_STATUS.CANCELLED,
  CANCELED: FINANCIAL_STATUS.CANCELLED,
  CANCELADO: FINANCIAL_STATUS.CANCELLED,
  CANCELADA: FINANCIAL_STATUS.CANCELLED
};

function normalizedKey(value: string) {
  return value
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\s-]+/g, "_")
    .toLocaleUpperCase("pt-BR");
}

export function normalizeFinancialStatus(value?: string | null): FinancialStatus | undefined {
  if (!value) return undefined;
  return aliases[normalizedKey(value)];
}

export function financialStatusApiCode(value?: string | null) {
  const status = normalizeFinancialStatus(value);
  const codes: Partial<Record<FinancialStatus, string>> = {
    PROGRAMADO: "SCHEDULED",
    PREVISTO: "FORECAST",
    EM_ABERTO: "OPEN",
    PARCIAL: "PARTIAL",
    PAGO: "PAID",
    RECEBIDO: "RECEIVED",
    VENCIDO: "OVERDUE",
    CANCELADO: "CANCELLED"
  };
  return status ? codes[status] : undefined;
}

export function financialStatusLabel(value?: string | null) {
  const status = normalizeFinancialStatus(value);
  return status ? FINANCIAL_STATUS_LABELS[status] : String(value || "")
    .replaceAll("_", " ")
    .toLocaleLowerCase("pt-BR")
    .replace(/(^|\s)\S/g, (letter) => letter.toLocaleUpperCase("pt-BR"));
}

export function effectiveFinancialStatus(input: {
  kind: AccountKind;
  storedStatus?: string | null;
  expectedCents: number;
  settledCents: number;
  dueDateKey: string;
  todayKey: string;
}): FinancialStatus {
  const stored = normalizeFinancialStatus(input.storedStatus);
  if (stored === FINANCIAL_STATUS.CANCELLED) return FINANCIAL_STATUS.CANCELLED;
  const balanceCents = Math.max(0, input.expectedCents - input.settledCents);
  if (balanceCents === 0) return input.kind === "payables" ? FINANCIAL_STATUS.PAID : FINANCIAL_STATUS.RECEIVED;
  if (input.dueDateKey < input.todayKey) return FINANCIAL_STATUS.OVERDUE;
  if (input.settledCents > 0) return FINANCIAL_STATUS.PARTIAL;
  if (input.dueDateKey > input.todayKey) return input.kind === "payables" ? FINANCIAL_STATUS.SCHEDULED : FINANCIAL_STATUS.FORECAST;
  return FINANCIAL_STATUS.OPEN;
}
